import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { FaxHooks, FaxQueries, FaxTypes, FaxV1 } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { Page } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Chip,
  ChipCombobox,
  DropdownField,
  ListRow,
  phone,
  SpinningLoader,
  Text,
  useAlert,
  useDebouncedValue,
  useFormField,
  useTooltip,
} from '@frontend/design-system';
import { PageScroller } from '../components/document-previewer/page-scroller';
import { LeaveConfirmationModal } from '../components/draft/LeaveConfirmationModal';
import { EmptyStateGraphic } from '../components/empty-state';
import { FaxCreationFooter } from '../components/fax-creation-footer/fax-creation-footer';
import { FaxCreationStepper } from '../components/stepper/fax-creation-stepper';
import { useAccessibleFaxNumbers } from '../hooks/use-accessible-fax-numbers';

type Contact = NonNullable<ReturnType<typeof FaxQueries.useQueryFaxContacts>['data']>[number];

export const FaxFinalizeDraftPage = ({ draftId }: { draftId: string }) => {
  const { selectedLocationId } = useSearch({ from: '/fax/drafts/$draftId/finalize', shouldThrow: false }) ?? {};
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationId || selectedLocationIds[0];
  const navigate = useNavigate();
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const faxAsyncSendFlag = getFeatureFlagValue('fax-async-send-flow');
  const { invalidateListFaxes, addToListFaxCache } = FaxV1.FaxQueryUpdaters.useListFaxQueryUpdater();
  const invalidateFaxSearch = FaxV1.FaxQueryUpdaters.useInvalidateFaxSearch();
  const invalidateDraftList = FaxDraftQueries.useInvalidateDraftList();
  const invalidateContacts = FaxHooks.useInvalidateFaxContacts();
  const invalidate = FaxDraftQueries.useInvalidateDraft();
  const [to, setTo] = useState<Contact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>();
  const [renderingContactId, setRenderingContactId] = useState<string[]>([]);
  const [pageScrollMultiplier, setPageScrollMultiplier] = useState<number | undefined>(0);
  const [showCustomMenuItem, setShowCustomMenuItem] = useState(false);
  const [contactSearchInput, setContactSearchInput] = useState('');
  const [comboboxKey, setComboboxKey] = useState(0);
  const debouncedContactSearchTerm = useDebouncedValue(contactSearchInput, 1000);
  const [blockNavigation, setBlockNavigation] = useState(true);

  const { show: isSlidePanelOpen, panelType: currentPanelType } = useSlidePanelShallowStore('show', 'panelType');

  useEffect(() => {
    if (isSlidePanelOpen && currentPanelType === 'faxContactForm') {
      setComboboxKey((prevKey) => prevKey + 1);
    }
  }, [isSlidePanelOpen, currentPanelType]);

  const {
    data: draft,
    isLoading: isLoadingDraft,
    refetch,
  } = FaxDraftQueries.useGetDraft(locationId, draftId, {
    enabled: !!draftId,
    keepPreviousData: true,
    refetchOnMount: true,
    retry: 2,
  });

  const { mutateAsync: addRecipient, isLoading: isAddRecipientLoading } = FaxDraftQueries.useAddRecipient({});
  const { mutateAsync: addRecipientByFaxNumber, isLoading: isAddRecipientByFaxNumberLoading } =
    FaxDraftQueries.useAddRecipientByFaxNumber({});
  const { mutateAsync: removeRecipient, isLoading: isRemoveRecipientLoading } = FaxDraftQueries.useRemoveRecipient({});
  const { mutate: setFromNumber } = FaxDraftQueries.useSetLocationFaxPhone({});
  const { mutateAsync: renderCoversheet, isLoading: isRendering } = FaxDraftQueries.useRenderCoversheet({});

  const isAddRemoveRecipientLoading =
    isAddRecipientLoading ||
    isRemoveRecipientLoading ||
    isAddRecipientByFaxNumberLoading ||
    !!renderingContactId.length;

  const { mutateAsync: mergeFinal, isLoading: isMerging } = FaxDraftQueries.useMergeFinal({});
  const { mutateAsync: sendFax, isLoading: isSending } = FaxDraftQueries.useSendFromDraft({
    onSuccess: () => {
      alert.success('Fax Sent!');
      setBlockNavigation(false);
      // Use a small delay to ensure state update is processed
      setTimeout(() => {
        navigate({ to: '/fax/$filter', params: { filter: 'inbox' } });
      }, 0);
    },
  });

  const { data: savedContacts } = FaxQueries.useQueryFaxContacts({
    locationId,
    locationIds: [locationId],
    limit: 5,
    recentContacts: true,
  });
  const { data: searchContactsData } = FaxQueries.useSearchContactsInfiniteQuery({
    locationId,
    locationIds: [locationId],
    query: debouncedContactSearchTerm,
    enabled: !!draftId && debouncedContactSearchTerm.length > 0,
    limit: 25,
  });
  const invalidateSearchContacts = FaxHooks.useInvalidateFaxSearchContacts();

  const allCurrentContacts = useMemo(() => {
    const savedContactsList = savedContacts ?? [];
    const searchedContactsList = searchContactsData?.pages?.flatMap((page) => page?.rows?.filter(Boolean)) ?? [];

    const seen = new Set<string>();
    const uniqueContacts: Contact[] = [];

    [...searchedContactsList, ...savedContactsList].forEach((contact) => {
      if (contact?.id && !seen.has(contact.id)) {
        seen.add(contact.id);
        uniqueContacts.push(contact);
      }
    });

    return uniqueContacts;
  }, [savedContacts, searchContactsData]);

  const { data: faxNumber } = FaxQueries.useQueryDefaultFaxNumber(locationId);
  const { data: numbers } = useAccessibleFaxNumbers([locationId]);

  const fromFieldProps = useFormField(
    {
      type: 'dropdown',
      value: (faxNumber || numbers?.[0]?.locationPhone) ?? '',
    },
    [faxNumber, numbers]
  );

  const setFromValue = (value?: string) => {
    fromFieldProps.onChange({ name: 'from', value: value ?? '' });
  };

  useEffect(() => {
    if (!fromFieldProps.value && numbers.length > 0) {
      setFromValue(faxNumber ?? numbers[0]?.locationPhone);
    }
  }, [numbers, faxNumber]);

  const totalSize =
    (draft?.uploadedFiles?.reduce((acc, file) => acc + parseInt(file.sizeBytes as any), 0) ?? 0) / 1000000;

  // auto adding valid number to recipient list
  useEffect(() => {
    if (debouncedContactSearchTerm.length === 10) {
      sendToNumber(debouncedContactSearchTerm);
      setComboboxKey((prevKey) => prevKey + 1);
    }
  }, [debouncedContactSearchTerm]);

  const addExistingContactAsRecipient = (existingContact: Contact) => {
    if (!draft?.id) return;
    setTo((prev) => [...prev, existingContact]);

    addRecipient({ draftId, locationId, contactId: existingContact.id })
      .then(() => {
        if (draft.coversheet?.html) {
          setRenderingContactId((prev) => [...prev, existingContact.id]);
          renderCoversheet({ draftId, locationId, contactId: existingContact.id }).then(() => {
            refetch()
              .then(() => {
                setSelectedContactId(existingContact.id);
              })
              .finally(() => {
                setRenderingContactId((prev) => prev.filter((id) => id !== existingContact.id));
              });
          });
        }
      })
      .catch(() => {
        setTo((prev) => prev.filter((contact) => contact.id !== existingContact.id));
        alert.error(t('Error adding recipient'));
      });
  };

  const addNewNumberAsRecipient = (value: string) => {
    if (!draft?.id) return;
    let number = value.replace(/[\s-()]/g, '');
    if (number.startsWith('+1')) {
      number = number.slice(2);
    } else if (number.startsWith('1')) {
      number = number.slice(1);
    }
    const isNumeric = /^\d{10}$/.test(number);
    if (isNumeric) {
      // Optimistically add unknown contact to recipients list
      const tempId = `temp-${Date.now()}-${value}`;
      const tempContact = {
        id: tempId,
        faxNumber: value,
        name: '',
        secondName: '',
        locationId,
        createdAt: '',
        updatedAt: '',
        deletedAt: '',
        isBlocked: false,
      } as Contact;

      setTo((prevTo) => [...prevTo, tempContact]);

      return addRecipientByFaxNumber({ draftId, locationId, contactFaxNumber: number })
        .then((res) => {
          const { recipients, locationId, createdAt } = res;
          invalidateContacts();
          invalidate(draftId, locationId, selectedLocationIds);
          let contact: Contact;

          if (recipients && recipients.length > 0) {
            const newNumber = recipients.find((recipient) => recipient.faxPhone.includes(number));

            contact = {
              id: newNumber?.contactId,
              faxNumber: newNumber?.faxPhone,
              name: '',
              secondName: '',
              locationId,
              createdAt,
              updatedAt: createdAt,
              deletedAt: '',
              isBlocked: false,
            } as Contact;

            if (draft.coversheet?.html) {
              setRenderingContactId((prev) => [...prev, contact.id]);
              renderCoversheet({ draftId, locationId, contactId: contact.id }).then(() => {
                refetch()
                  .then(() => {
                    setSelectedContactId(contact.id);
                  })
                  .finally(() => {
                    setRenderingContactId((prev) => prev.filter((id) => id !== contact.id));
                  });
              });
            }
          }

          setTo((prev) => [...prev.filter((contact) => contact.id !== tempId), contact]);
          invalidateSearchContacts({ limit: 5 });
        })
        .catch((e) => {
          setTo((prev) => prev.filter((contact) => contact.id !== tempId));
          if (e.message.includes('invalid fax number')) {
            alert.error(t('Invalid fax number'));
          } else {
            alert.error(t('Error adding contact'));
          }
        });
    } else {
      return alert.error(t('Invalid fax number'));
    }
  };

  // Adding custom number to recipient list
  const sendToNumber = (value: string) => {
    if (!draft?.id) {
      alert.error(t('Error adding recipient'));
      return;
    }
    if (to.find((contact) => contact.faxNumber === value)) {
      alert.error(t('Duplicate recipients are not allowed'));
      return;
    }
    const existingContact = allCurrentContacts?.find(
      (contact) =>
        contact.faxNumber === value ||
        `${contact.name} ${contact.secondName}`.toLowerCase().includes(value.toLowerCase())
    );
    if (existingContact && existingContact.id) {
      addExistingContactAsRecipient(existingContact);
      return;
    }
    addNewNumberAsRecipient(value);
  };

  // TODO: modify this logic to a better one
  useEffect(() => {
    const recipients = draft?.recipients ?? [];
    if (isAddRemoveRecipientLoading || isLoadingDraft || isRendering) return;
    if (recipients?.length && allCurrentContacts?.length) {
      const selected = recipients
        .map((recipient) => {
          const contact = allCurrentContacts.find((contact) => contact.id === recipient.contactId);
          if (!contact) {
            return {
              id: recipient.contactId,
              faxNumber: recipient?.faxPhone,
              name: recipient?.name,
              secondName: recipient?.secondName,
              locationId,
              createdAt: '',
              updatedAt: '',
              deletedAt: '',
              isBlocked: false,
            } as Contact;
          }
          return contact;
        })
        .filter((contact): contact is NonNullable<typeof contact> => !!contact && !!contact.id);
      setTo((prev) => {
        const newContacts = selected.filter((contact) => !prev.find((prevContact) => prevContact.id === contact.id));
        return [...prev, ...newContacts];
      });
    }
  }, [draft?.recipients, allCurrentContacts]);

  useEffect(() => {
    if (draft?.location?.faxPhone && numbers?.length) {
      const selected = numbers.find((num) => num.locationPhone === draft.location?.faxPhone);
      setFromValue(selected?.locationPhone);
    }
  }, [draft?.location?.faxPhone, numbers]);

  // const previewFax = async () => {
  //   if (!draft?.locationId) {
  //     return;
  //   }

  //   setIsLoadingPreview(true);
  //   const mediaId =
  //     preview?.previews[0]?.mediaId ??
  //     (await refetchPreview().catch(() => ({ data: undefined }))).data?.previews[0]?.mediaId;

  //   if (!mediaId) {
  //     setIsLoadingPreview(false);
  //     return alert.error(t('Preview not available'));
  //   } else {
  //     return FaxApi.getDraftPDFBlob(mediaId)
  //       .then((res) => {
  //         const url = URL.createObjectURL(res);
  //         window.open(url, '_blank');
  //       })
  //       .catch((_err) => {
  //         alert.error(t('Error generating preview'));
  //       })
  //       .finally(() => setIsLoadingPreview(false));
  //   }
  // };

  const onStepClick = (stepIndex: number) => {
    const step = stepIndex + 1;
    if (step === 1) {
      return navigate({
        to: '/fax/drafts/$draftId/begin',
        params: { draftId },
        search: { selectedLocationId },
      });
    }
    if (step === 2) {
      navigate({
        to: '/fax/drafts/$draftId/cover',
        params: { draftId },
        search: { selectedLocationId },
      });
    }
    if (step === 3) {
      navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId },
        search: { selectedLocationId },
      });
    }
    return;
  };

  const send = async () => {
    if (!draft?.locationId) {
      return alert.error(t('Invalid location id'));
    }
    if (!fromFieldProps.value && !faxNumber) {
      return alert.error(t('Please select a from number'));
    }
    if (!draft.recipients?.length) {
      return alert.error(t('Please select at least one recipient'));
    }

    return Promise.allSettled(
      draft.recipients.map((recipient) =>
        mergeFinal({ draftId, locationId: draft?.locationId ?? '', contactId: recipient.contactId })
      )
    ).then((allResults) => {
      const rejected = allResults.filter((res) => res.status === 'rejected');
      const message = rejected.length
        ? t(`Final rendering failed for {{contacts}}`, {
            contacts: allResults.map((_res, i) => draft.recipients?.[i]?.name).join(', '),
          })
        : undefined;
      if (message) {
        alert.error(message);
      }
      sendFax({ draftId, locationId: draft?.locationId ?? '' })
        .then(({ faxes }) => {
          if (!faxes || !faxes.length) {
            invalidateListFaxes();
          } else {
            if (!faxAsyncSendFlag) {
              faxes.forEach((fax) => {
                addToListFaxCache({ faxId: fax.id, locationId: fax.locationId });
              });
            }
          }
          invalidateDraftList();
          invalidateContacts();
          invalidateSearchContacts({ limit: 5 });
          invalidateFaxSearch();
        })
        .catch((e) => {
          const errorString = e.message ?? '';
          if (errorString.includes('recipient fax number is invalid')) {
            alert.error(t('Invalid fax number in recipient list'));
          } else {
            alert.error(t('Error sending fax'));
          }
        });
    });
  };

  const updateFromField = (number: string) => {
    if (!draft?.id) {
      return;
    }
    setFromNumber({ locationId, draftId: draft.id, faxPhone: number ?? '' });
  };

  const updateToField = async (tags: Contact[]) => {
    if (!allCurrentContacts?.length || !draft?.id) {
      alert.error(t('Error adding recipient'));
      return;
    }
    const uniqueIdCount = new Set(tags.map((contact) => contact.id)).size;
    if (uniqueIdCount !== tags.length) {
      alert.error(t('Duplicate recipients are not allowed'));
      return;
    }
    const ids = tags.map((tag) => tag.id);
    const recipients = draft.recipients?.map((recipient) => recipient.contactId) ?? [];
    const added = ids
      .filter((id) => !recipients.includes(id))
      .map((id) => allCurrentContacts.find((contact) => contact.id === id))
      .filter((contact): contact is NonNullable<typeof contact> => !!contact);
    const removed = recipients.filter((id) => !ids.includes(id));

    setTo(tags);
    await Promise.all([
      ...added.map((contact) => addRecipient({ draftId, locationId, contactId: contact.id })),
      ...removed.map((id) => removeRecipient({ draftId, locationId, contactId: id })),
    ]);

    if (draft.coversheet?.html) {
      setRenderingContactId(added.map((contact) => contact.id));
      await Promise.all(
        added.map(async (contact) => {
          const res = await renderCoversheet({ draftId, locationId, contactId: contact.id });
          setRenderingContactId((prev) => prev.filter((id) => id !== contact.id));
          return res;
        })
      );
    } else Promise.resolve();

    return refetch().then(() => {
      if (added.length) {
        setSelectedContactId(added[added.length - 1].id);
      } else {
        if (tags.length > 0) {
          setSelectedContactId(tags[0].id);
        } else {
          setSelectedContactId(undefined);
        }
      }
    });
  };

  const selectContact = (contact: Contact) => {
    setSelectedContactId(contact.id);
  };

  const customOptionsFilter = useCallback(
    (contact: Contact, inputValue: string) => {
      let formattedInput = inputValue.replace(/[\s-()]/g, '').toLowerCase();
      if (formattedInput.startsWith('+1')) {
        formattedInput = formattedInput.slice(2);
      } else if (formattedInput.startsWith('1')) {
        formattedInput = formattedInput.slice(1);
      }

      if (formattedInput.length === 10 && /^\d{10}$/.test(formattedInput)) {
        const filteredContacts = allCurrentContacts?.filter(
          (contact) => contact.faxNumber?.replace(/[\s-()]/g, '') === formattedInput
        );
        if (filteredContacts?.length === 0) {
          setShowCustomMenuItem(true);
        } else {
          setShowCustomMenuItem(false);
        }
      } else {
        setShowCustomMenuItem(false);
      }

      const value = `${contact.name ?? ''} ${contact.secondName ?? ''} ${contact.faxNumber}`.replace(/[\s-()]/g, '');

      return value.toLowerCase().includes(formattedInput);
    },
    [allCurrentContacts]
  );

  const previewThumbnailIds = useMemo(() => {
    const mediaIds = [];
    const isHTML = !!draft?.coversheet?.html;
    const genericHtmlCoversheetThumbnailId = isHTML ? draft?.coversheet?.largeThumbnailId : undefined;
    const contactHtmlCoversheetThumbnailId =
      isHTML && ((draft?.recipients && draft?.recipients?.length === 1) || selectedContactId)
        ? draft?.recipientCoversheets?.find(
            (rc) => rc.contactId === (selectedContactId || draft?.recipients?.[0].contactId)
          )?.largeThumbnailId
        : undefined;
    const htmlCoversheetThumbnailId = contactHtmlCoversheetThumbnailId ?? genericHtmlCoversheetThumbnailId;

    if (isHTML && htmlCoversheetThumbnailId) {
      mediaIds.push(htmlCoversheetThumbnailId);
    }
    const uploadedCoversheetThumbnailIds = draft?.uploadedCoversheet?.largeThumbnailIds;
    if (!isHTML && uploadedCoversheetThumbnailIds) {
      mediaIds.push(...uploadedCoversheetThumbnailIds);
    }

    const uploadedFilesThumbnailIds = draft?.uploadedFiles?.map((file) => file.largeThumbnailIds).flat();
    if (uploadedFilesThumbnailIds?.length) {
      mediaIds.push(...uploadedFilesThumbnailIds);
    }
    return mediaIds;
  }, [
    draft?.coversheet?.html,
    draft?.recipientCoversheets,
    draft?.uploadedCoversheet?.largeThumbnailIds,
    draft?.uploadedFiles,
    selectedContactId,
  ]);

  useEffect(() => {
    setPageScrollMultiplier(0);
  }, [draft?.id]);

  useEffect(() => {
    if (fromFieldProps.value) {
      updateFromField(fromFieldProps.value);
    }
  }, [fromFieldProps.value]);

  // const _selectedContact = useMemo(
  //   () => (selectedContactId ? contacts?.find((contact) => contact.id === selectedContactId) : undefined),
  //   [selectedContactId, contacts]
  // );
  //
  //

  const optionsToShow = useMemo(() => {
    return (
      (debouncedContactSearchTerm
        ? searchContactsData?.pages?.flatMap((page) => page?.rows?.filter(Boolean))
        : savedContacts) ?? []
    );
  }, [debouncedContactSearchTerm, savedContacts, searchContactsData]);

  if (!draft) {
    return (
      <Page
        showBackBtn
        title={t('Draft Not Found')}
        customBackFn={() => {
          navigate({ to: '/fax/$filter', params: { filter: 'drafts' }, search: { selectedLocationId } });
        }}
        loading={isLoadingDraft}
      >
        <EmptyStateGraphic />
      </Page>
    );
  }

  return (
    <section
      css={css`
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      <header
        css={css`
          border-bottom: 1px solid ${theme.colors.neutral20};
          padding: ${isMobile ? theme.spacing(2) : theme.spacing(3)} 0;
        `}
      >
        <FaxCreationStepper
          currentStep={4}
          onStepClick={onStepClick}
          onSend={send}
          sendLoading={isSending || isMerging}
          sendDisabled={
            !draft ||
            !previewThumbnailIds.length ||
            isLoadingDraft ||
            isRendering ||
            isSending ||
            isMerging ||
            draft?.recipients?.length === 0
          }
        />
      </header>
      <div
        css={css`
          border-bottom: 1px solid ${theme.colors.neutral20};
          padding: ${theme.spacing(3)};
          > div {
            max-width: 740px;
            &:nth-of-type(1) {
              margin-bottom: ${theme.spacing(2)};
            }
          }
        `}
      >
        {optionsToShow && numbers ? (
          <>
            <ChipCombobox<Contact>
              key={comboboxKey}
              label={t('To:')}
              clearable={false}
              disabled={isLoadingDraft || isAddRemoveRecipientLoading || isRendering}
              autoFocus={true}
              options={
                !!optionsToShow?.length
                  ? optionsToShow
                  : [
                      {
                        id: 'no-contacts',
                        faxNumber: '',
                        phoneNumber: '',
                        name: '',
                        secondName: '',
                        locationId: '',
                        createdAt: '',
                        updatedAt: '',
                        deletedAt: '',
                        isBlocked: false,
                      },
                    ]
              }
              tags={to}
              name='to'
              onTagsChange={updateToField}
              MenuItem={FaxContactItem}
              ChipComponent={ContactChip}
              accessor={(contact) => `${contact.name ?? ''} ${contact.secondName ?? ''} ${contact.faxNumber}`}
              itemHeight={62}
              itemWidth={'100%'}
              menuProps={{
                placement: 'bottom-start',
              }}
              menuStyles={css`
                width: min(100vw, 500px);
              `}
              allowCustomSubmission={showCustomMenuItem}
              onCustomSubmission={sendToNumber}
              CustomSubmissionMenuItem={AddContactMenuItem}
              tabComplete
              customOptionsFilter={customOptionsFilter}
              onInputChange={(inputValue) => setContactSearchInput(inputValue)}
              startAdornment={
                !!(isLoadingDraft && draft) || isAddRemoveRecipientLoading || isRendering ? (
                  <SpinningLoader size='xs' color='subdued' css={{ position: 'absolute', right: theme.spacing(0.5) }} />
                ) : undefined
              }
            />
            <DropdownField {...fromFieldProps} name='from' label={t('From:')} disabled={numbers.length === 1}>
              {numbers.map((faxNumber) => (
                <DropdownField.Option key={faxNumber.locationPhone} value={faxNumber.locationPhone}>
                  <FromNumberOption locationPhone={faxNumber.locationPhone} displayName={faxNumber.displayName} />
                </DropdownField.Option>
              ))}
            </DropdownField>
          </>
        ) : null}
      </div>
      <div
        css={css`
          display: flex;
          background: ${theme.colors.neutral5};
          overflow: hidden;
          height: 100%;
          flex: 1;
          justify-content: center;
          align-items: center;
        `}
      >
        {!!previewThumbnailIds.length ? (
          <>
            {to?.length > 1 && draft?.coversheet?.html && (
              <ul
                css={css`
                  width: 224px;
                  margin: 0;
                  padding: 0;
                  background: ${theme.colors.white};
                  height: 100%;
                `}
              >
                {to?.map((contact) => (
                  <ListRow
                    key={contact.id}
                    isSelected={contact.id === selectedContactId}
                    onClick={isRendering ? undefined : () => selectContact(contact)}
                  >
                    <ListRow.Content>
                      {contact.name || phone(contact.faxNumber, { hideCountry: true })}{' '}
                      {!contact.name ? '' : contact.secondName ?? ''}
                    </ListRow.Content>
                    {isLoadingDraft ||
                      (isRendering && !!renderingContactId.length && renderingContactId.includes(contact.id) && (
                        <ListRow.Trail>
                          <SpinningLoader size='xs' color='subdued' />
                        </ListRow.Trail>
                      ))}
                  </ListRow>
                ))}
              </ul>
            )}
            <PageScroller
              pageScrollMultiplier={pageScrollMultiplier}
              setPageScrollMultiplier={setPageScrollMultiplier}
              mediaIds={previewThumbnailIds}
              locationId={locationId}
            />
          </>
        ) : (
          <EmptyStateGraphic />
        )}
      </div>
      <FaxCreationFooter
        totalSize={!!(isLoadingDraft && draft) || isRendering || isSending || isMerging ? 'loading' : totalSize}
      />
      <LeaveConfirmationModal draftId={draftId} shouldBlock={blockNavigation} locationId={locationId} />
    </section>
  );
};

type FaxContactItemProps = { item: Contact };
const FaxContactItem = ({ item: { name, secondName, faxNumber, profileMediaId } }: FaxContactItemProps) => {
  const { t } = useTranslation('fax');

  if (!name && !faxNumber && !secondName && !profileMediaId) {
    return (
      <ListRow
        style={{ padding: theme.spacing(1) }}
        onClick={(e: React.MouseEvent<HTMLLIElement>) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <ListRow.Lead>
          <Avatar
            size='small'
            src={undefined}
            css={css`
              svg {
                margin-right: 0;
              }
              figure {
                font-size: 0.6em;
              }
            `}
          />
        </ListRow.Lead>
        <ListRow.Content css={{ textAlign: 'initial' }}>
          <Text>{t('Start by adding a recipient!')}</Text>
        </ListRow.Content>
      </ListRow>
    );
  }

  return (
    <ListRow style={{ padding: theme.spacing(1), cursor: 'pointer' }}>
      <ListRow.Lead>
        <Avatar
          size='small'
          src={profileMediaId || undefined}
          css={css`
            svg {
              margin-right: 0;
            }
            figure {
              font-size: 0.6em;
            }
          `}
        />
      </ListRow.Lead>
      <ListRow.Content css={{ textAlign: 'initial' }}>
        <Text weight='bold'>{`${name ?? ''} ${secondName ?? ''}`}</Text>
        <Text color='subdued' size={'medium'}>
          {faxNumber ? formatPhoneNumber(faxNumber) : t('No Phone Number')}
        </Text>
      </ListRow.Content>
    </ListRow>
  );
};

const ContactChip = ({
  children,
  onClick,
  disabled,
}: {
  children?: string;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  const { selectedLocationId } = useSearch({ from: '/fax/drafts/$draftId/finalize', shouldThrow: false }) ?? {};
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationId || selectedLocationIds[0];
  const { setShow } = useSlidePanelShallowStore('setShow');
  const alert = useAlert();
  const { t } = useTranslation('fax');

  const childrenArray = children && children.length > 0 ? children.trim().split(' ') : [];
  const contactNumber = childrenArray.at(-1) ?? '';
  const contactName = childrenArray.slice(0, -1).join(' ') ?? '';

  return (
    <div
      onClick={() => {
        if (disabled) return;
        if (contactNumber) {
          setShow(true, 'faxContactForm', {
            contactNumber,
            locationId: locationId,
            action: FaxTypes.ContactAction.EDIT,
          });
        } else {
          alert.error(t('Error rendering contact data!'));
        }
      }}
    >
      <Chip.Removable
        css={css`
          width: auto;
          max-width: 160px;
          cursor: pointer;
        `}
        variant={!!disabled ? 'disabled' : 'default'}
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          onClick?.();
        }}
        disabled={!!disabled}
        leftElement={<Avatar name={/^\d+$/.test(contactName.trim()) ? '' : contactName} size='xxs' />}
      >
        {contactName
          ? contactName
          : /^\d+$/.test(contactNumber.trim())
          ? phone(contactNumber, { hideCountry: true })
          : t('Invalid Contact')}
      </Chip.Removable>
    </div>
  );
};

const AddContactMenuItem = ({ value }: { value: string }) => {
  let number = value.replace(/[\s-()]/g, '');
  if (number.startsWith('+1')) {
    number = number.slice(2);
  } else if (number.startsWith('1')) {
    number = number.slice(1);
  }

  return (
    <>
      <li
        css={{
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        {phone(value, { hideCountry: true })}
      </li>
    </>
  );
};

type FromNumberOptionProps = {
  locationPhone: string;
  displayName?: string;
};

const FromNumberOption = ({ locationPhone, displayName }: FromNumberOptionProps) => {
  const {
    Tooltip: FaxNumberTooltip,
    triggerProps: faxNumberTooltipTriggerProps,
    tooltipProps: faxNumberTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'right-end',
    offset: 8,
  });

  const formattedNumber = formatPhoneNumber(locationPhone);
  const displayText = displayName ? `# ${displayName}` : `# ${formattedNumber}`;

  return (
    <>
      <div css={{ display: 'flex', alignItems: 'center', width: '100%' }} {...faxNumberTooltipTriggerProps}>
        {displayText}
      </div>
      {displayName && (
        <FaxNumberTooltip {...faxNumberTooltipProps}>
          {displayName}
          <br />
          {formattedNumber}
        </FaxNumberTooltip>
      )}
    </>
  );
};
